import type { TiptapDoc } from './types';

interface TiptapNode {
    type?: string;
    text?: string;
    content?: TiptapNode[];
}

function inlineToText(nodes: TiptapNode[] | undefined): string {
    if (!nodes) return '';
    return nodes
        .map((n) => {
            if (n.type === 'text' && typeof n.text === 'string') return n.text;
            if (n.type === 'hardBreak') return '\n';
            return '';
        })
        .join('');
}

function blockToText(node: TiptapNode): string {
    switch (node.type) {
        case 'paragraph':
            return inlineToText(node.content);

        case 'heading':
            return inlineToText(node.content);

        case 'bulletList':
        case 'orderedList': {
            const items = (node.content ?? []).map((li) => {
                const body = (li.content ?? []).map(blockToText).join('\n').trim();
                return `• ${body}`;
            });
            return items.join('\n');
        }

        case 'listItem':
            return (node.content ?? []).map(blockToText).join('\n');

        case 'blockquote':
            return (node.content ?? []).map(blockToText).join('\n');

        case 'image':
            return '';

        case 'imageGrid':
            return '';

        case 'horizontalRule':
            return '';

        default:
            return (node.content ?? []).map(blockToText).join('\n');
    }
}

/**
 * Projects a TiptapDoc to a plain-text string suitable for sending to
 * Monday.com / DentalMonitoring via the backend `notes` field.
 *
 * Images are omitted. Block boundaries produce "\n\n". Hard breaks produce "\n".
 */
export function tiptapDocToPlainText(doc: TiptapDoc): string {
    if (!doc?.content) return '';
    const blocks = doc.content as TiptapNode[];
    const parts = blocks.map(blockToText).filter((t) => t.length > 0);
    return parts.join('\n\n').replace(/[ \t]+$/gm, '');
}

/** Builds a minimal doc from legacy plain text: one paragraph per line. */
export function docFromPlainText(text: string): TiptapDoc {
    return {
        type: 'doc',
        content: text
            .split('\n')
            .map((line) =>
                line.length > 0
                    ? { type: 'paragraph', content: [{ type: 'text', text: line }] }
                    : { type: 'paragraph' }
            )
    };
}

/** Builds a doc from legacy plain text: blank lines split paragraphs, single newlines become hard breaks. */
export function tiptapDocFromString(text: string): TiptapDoc {
    const trimmed = (text ?? '').replace(/\r\n/g, '\n');
    if (trimmed.trim() === '') return { type: 'doc', content: [{ type: 'paragraph' }] };

    const paragraphs = trimmed.split(/\n\n+/);
    return {
        type: 'doc',
        content: paragraphs.map((para) => {
            const lines = para.split('\n');
            const inline: unknown[] = [];
            lines.forEach((line, i) => {
                if (i > 0) inline.push({ type: 'hardBreak' });
                if (line.length > 0) inline.push({ type: 'text', text: line });
            });
            return inline.length > 0
                ? { type: 'paragraph', content: inline }
                : { type: 'paragraph' };
        })
    };
}
